import React from "react";
import { Bed, Bath, Car, Square, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Home } from "../types";
import { getHomeDisplayName } from "../lib/homes";

interface HomeCardProps {
  home: Home;
  showCategory?: boolean;
  featured?: boolean;
}

const HomeCard: React.FC<HomeCardProps> = ({
  home,
  showCategory = true,
  featured = false,
}) => {
  const categoryColors = {
    core: "bg-blue-100 text-blue-800",
    family: "bg-green-100 text-green-800",
    kiwiana: "bg-amber-100 text-amber-800",
  };

  const categoryNames = {
    core: "Core",
    family: "Family",
    kiwiana: "Kiwiana",
  };

  return (
    <Link
      to={`/homes/${home.category}/${home.slug}`}
      className="inline-flex items-center space-x-2 text-[#FF5A1F] hover:text-[#e4501c] font-semibold transition-colors duration-200 group/link"
    >
      <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1">
        {/* Featured Badge */}
        {featured && (
          <div className="absolute top-4 left-4 z-10 bg-[#FF5A1F] text-white px-3 py-1 rounded-full text-sm font-semibold">
            Featured
          </div>
        )}

        {/* Category Badge */}
        {showCategory && (
          <div
            className={`absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-sm font-medium ${
              categoryColors[home.category]
            }`}
          >
            {categoryNames[home.category]}
          </div>
        )}

        {/* Image */}
        <div className="relative h-64 overflow-hidden">
          <img
            src={home.heroImage}
            alt={home.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-xl font-bold text-[#1F2937] group-hover:text-[#FF5A1F] transition-colors duration-200">
              {getHomeDisplayName(home)}
            </h3>
          </div>

          {/* Specs */}
          <div className="flex items-center space-x-4 mb-4 text-[#334155]">
            <div className="flex items-center space-x-1">
              <Bed className="w-4 h-4" />
              <span className="text-sm font-medium">{home.specs.bedrooms}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Bath className="w-4 h-4" />
              <span className="text-sm font-medium">
                {home.specs.bathrooms}
              </span>
            </div>
            {home.specs.garage && (
              <div className="flex items-center space-x-1">
                <Car className="w-4 h-4" />
                <span className="text-sm font-medium">{home.specs.garage}</span>
              </div>
            )}
            <div className="flex items-center space-x-1">
              <Square className="w-4 h-4" />
              <span className="text-sm font-medium">
                {home.specs.totalArea}m²
              </span>
            </div>
          </div>

          {/* Key Features */}
          <div className="mb-4">
            <p className="text-sm text-[#334155] line-clamp-2">
              {home.keyFeatures.slice(0, 2).join(" • ")}
            </p>
          </div>

          {/* CTA */}
          <div className="flex items-center space-x-2">
            <span>View Details</span>
            <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-200" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default HomeCard;
